/**
 * Kids passports — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tickets_play`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const kidsPassportsPack: ToilePack = {
    slug: 'kids-passports',
    category: 'classroom',
    name: 'Kids passports',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'cover', label: 'Passport cover', subject: 'a passport cover with a globe emblem, "MY PASSPORT" in gold capitals and a blank line for the traveller\'s name'},
        {key: 'identity', label: 'Identity page', subject: 'a passport identity page with an empty photo box, blank labelled fields for name, age, birthday and home country, and a fingerprint box, labelled "PASSPORT"'},
        {key: 'stamps', label: 'Stamp pages', subject: 'a pair of visa pages with empty boxes for country stamps, a few round stamp outlines and lines for country and date, labelled "VISA"'},
    ],
    styles: [
        {key: 'classic', label: 'Classic passport', style: 'a classic passport booklet design with a guilloche pattern and fine rules', palette: 'navy and gold on cream', framing: 'the page centered with a wide margin'},
        {key: 'world', label: 'World map', style: 'a colourful world-map passport design with simple continents and rounded icons', palette: 'teal, orange and sand on white', framing: 'the page centered with a wide margin'},
        {key: 'mono', label: 'Black and white', style: 'a simple black line print with large clear shapes and plain lettering', palette: 'black ink only', framing: 'the page centered with a wide white margin'},
    ],
    directions: ['red cover', 'country list', 'space travel'],
    directionHint: 'try: red cover',
    seo: {
        title: 'Kids Passport Template: Cover, Identity and Visa Pages',
        metaDescription: 'Nine printable kids passport pages: a cover, an identity page with a photo box and visa pages to stamp, in three styles.',
        h1: 'Kids passport template: passport cover, identity page, visa stamp pages',
        tagline: 'Nine passport pages, three parts of the booklet, each in a classic, world map or black-and-white style.',
        intro: 'Teachers use a kids passport for a world-cultures unit and parents for a pretend trip, stamping a page for every country visited. The pages are a cover, an identity page with a photo box, and visa pages to stamp. The nine pictures show these parts in a classic navy and gold guilloche design, a colourful world-map print, and a plain black line print with empty boxes. Type a different theme and the same pages are drawn for it.',
        useCases: [
            'a world cultures unit in a classroom',
            'a pretend family trip at home',
            'a reading or geography activity',
            'a travel-themed gift for a child',
        ],
        faq: [
            {q: 'Are the stamps and the photo already on the page?', a: 'No. The visa pages have empty boxes for stamps, with a few round outlines to stamp over. The identity page has an empty photo box.'},
            {q: 'Is this a real passport?', a: 'No. It is a pretend booklet for play and learning, and it is not an identity document.'},
            {q: 'What is the difference between the three styles?', a: 'Classic passport is navy and gold on cream with a guilloche pattern. World map uses teal, orange and sand with simple continents. Black and white is a plain line print with large shapes.'},
        ],
    },
}
